import React, { forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormRef } from "types/form";
import TextBox from "components/TextBox";
import ListBox from "components/ComboBox";
import ROLES from "./Roles";
import INDUSTRIES from "./Industries";
import { RootState } from "store";
import {
  setCompany,
  setFirstName,
  setLastName,
  setPhoneNumber,
  setIndustry,
  setRole,
  setFormErrors,
  SignUpState,
} from "store/signUpSlice";
import { profileSchema } from "validation/schemas/profile";
import ComboBoxItem from "components/ComboBox/ComboBoxItem";

import styles from "./ProfileForm.module.scss";

const ProfileForm = forwardRef<FormRef>((props, ref) => {
  const dispatch = useDispatch();
  const signUpState: SignUpState = useSelector(
    (state: RootState) => state.signUp,
  );

  const clearErrors = () => {
    if (signUpState.errors?.profile) {
      dispatch(
        setFormErrors({
          ...signUpState.errors,
          profile: undefined,
        }),
      );
    }
  };

  const onFormChange =
    <T,>(onChange: (value: T) => void) =>
    (value: T) => {
      clearErrors();
      onChange(value);
    };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      clearErrors();
      const result = profileSchema.safeParse({
        firstName: signUpState.firstName,
        lastName: signUpState.lastName,
        phoneNumber: signUpState.phoneNumber,
        company: signUpState.company,
        industry: signUpState.industry,
        role: signUpState.role,
      });

      if (!result.success) {
        dispatch(
          setFormErrors({
            profile: result.error.errors.reduce(
              (acc, curr) => {
                acc[curr.path[0]] = curr.message;
                return acc;
              },
              {} as Record<string, string>,
            ),
          }),
        );
        return false;
      }

      dispatch(setFormErrors({}));
      return true;
    },
  }));

  return (
    <div className={styles.form}>
      <div className={styles.field}>
        <div className={styles.label}>First Name</div>
        <TextBox
          lean
          placeholder="Brandon"
          value={signUpState.firstName}
          onChange={onFormChange((value) => dispatch(setFirstName(value)))}
          error={!!signUpState.errors?.profile?.firstName}
        />
        {signUpState.errors?.profile?.firstName && (
          <div className={styles.errorText}>
            {signUpState.errors.profile.firstName}
          </div>
        )}
      </div>

      <div className={styles.field}>
        <div className={styles.label}>Last Name</div>
        <TextBox
          lean
          placeholder="Noor"
          value={signUpState.lastName}
          onChange={onFormChange((value) => dispatch(setLastName(value)))}
          error={!!signUpState.errors?.profile?.lastName}
        />
        {signUpState.errors?.profile?.lastName && (
          <div className={styles.errorText}>
            {signUpState.errors.profile.lastName}
          </div>
        )}
      </div>

      <div className={styles.field}>
        <div className={styles.label}>Phone Number</div>
        <TextBox
          lean
          placeholder="+65 90010101"
          value={signUpState.phoneNumber}
          onChange={onFormChange((value) => dispatch(setPhoneNumber(value)))}
          error={!!signUpState.errors?.profile?.phoneNumber}
        />
        {signUpState.errors?.profile?.phoneNumber && (
          <div className={styles.errorText}>
            {signUpState.errors.profile.phoneNumber}
          </div>
        )}
      </div>

      <div className={styles.field}>
        <div className={styles.label}>Company / Organisation</div>
        <TextBox
          lean
          placeholder="a company"
          value={signUpState.company}
          onChange={onFormChange((value) => dispatch(setCompany(value)))}
          error={!!signUpState.errors?.profile?.company}
        />
        {signUpState.errors?.profile?.company && (
          <div className={styles.errorText}>
            {signUpState.errors.profile.company}
          </div>
        )}
      </div>

      <div className={styles.field}>
        <div className={styles.label}>Industry</div>
        <ListBox
          lean
          selected={signUpState.industry}
          options={INDUSTRIES}
          onChange={onFormChange((value: ComboBoxItem<string>) =>
            dispatch(setIndustry(value)),
          )}
          error={!!signUpState.errors?.profile?.industry}
        />
        {signUpState.errors?.profile?.industry && (
          <div className={styles.errorText}>
            {signUpState.errors.profile.industry}
          </div>
        )}
      </div>

      <div className={styles.field}>
        <div className={styles.label}>Role</div>
        <ListBox
          lean
          selected={signUpState.role}
          options={ROLES}
          onChange={onFormChange((value: ComboBoxItem<string>) =>
            dispatch(setRole(value)),
          )}
          error={!!signUpState.errors?.profile?.role}
        />
        {signUpState.errors?.profile?.role && (
          <div className={styles.errorText}>
            {signUpState.errors.profile.role}
          </div>
        )}
      </div>
    </div>
  );
});

ProfileForm.displayName = "ProfileForm";

export default ProfileForm;
